import { checkoutFetch, withCheckoutDeadline } from './checkoutDeadline.js';

const MAX_CONCURRENT_CHECKOUT_UPLOADS = 3;

export async function uploadCheckoutFilesToCloudinary({ orderId, uploads, fetchImpl = globalThis.fetch, timeoutMs = 60000 }) {
  try {
    return await withCheckoutDeadline(async (signal) => {
      const fetchBounded = (input, init = {}) => checkoutFetch(input, { ...init, signal }, { fetcher: fetchImpl, timeoutMs });
      if (!orderId) return { ok: false, skipped: true, reason: 'missing_order_id', files: [] };
      if (!uploads || !uploads.length) return { ok: true, skipped: true, reason: 'no_uploads', files: [] };

      const uploaded = new Array(uploads.length);
      const prepared = new Array(uploads.length);
      let nextIndex = 0;
      let skippedReason = '';
      let skippedIndex = Infinity;

      const recordFailure = (entry, index, error) => {
        uploaded[index] = {
          orderId,
          productSku: entry.product,
          productName: entry.productName || '',
          areaKey: entry.areaKey,
          method: entry.method,
          itemIndex: entry.itemIndex,
          originalName: entry.fileName,
          error: error?.message || String(error),
        };
      };

      const signNextFile = async () => {
        while (!signal.aborted && !skippedReason && nextIndex < uploads.length) {
          const index = nextIndex;
          nextIndex += 1;
          const entry = uploads[index];
          try {
            const resolved = entry.dataUrl;
            if (!resolved) throw new Error('missing_data_url');

            const nameLower = String(entry.fileName || '').toLowerCase();
            const isPdf = nameLower.endsWith('.pdf') || String(resolved).startsWith('data:application/pdf');
            const sigResp = await fetchBounded('/api/cloudinary/sign', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                orderId,
                productSku: entry.product || 'general',
                itemIndex: entry.itemIndex,
                areaKey: entry.areaKey || entry.method || 'file',
                originalName: entry.fileName || 'file',
              }),
            });
            const sigJson = await sigResp.json().catch(() => null);
            if (sigJson?.skipped) {
              if (index < skippedIndex) {
                skippedIndex = index;
                skippedReason = sigJson.reason || 'cloudinary_skipped';
              }
              continue;
            }
            if (!sigJson || sigJson.ok !== true) {
              throw new Error(sigJson?.error || 'cloudinary_sign_failed');
            }
            prepared[index] = { entry, index, resolved, isPdf, signature: sigJson };
          } catch (error) {
            recordFailure(entry, index, error);
          }
        }
      };

      const signedWorkers = await Promise.allSettled(Array.from(
        { length: Math.min(MAX_CONCURRENT_CHECKOUT_UPLOADS, uploads.length) },
        () => signNextFile(),
      ));
      const failedSigningWorker = signedWorkers.find((worker) => worker.status === 'rejected');
      if (failedSigningWorker) return { ok: false, error: failedSigningWorker.reason, files: [] };
      if (skippedReason) return { ok: false, skipped: true, reason: skippedReason, files: [] };

      // The signer may assign repeated cart lines (or sanitized filenames) the
      // same target. Build groups only after all signatures are known so their
      // completion order cannot reverse the original overwrite order.
      const targetGroups = new Map();
      prepared.forEach((file) => {
        const target = JSON.stringify([
          file.signature.cloudName, file.signature.folder, file.signature.public_id,
        ]);
        if (!targetGroups.has(target)) targetGroups.set(target, []);
        targetGroups.get(target).push(file);
      });
      const groups = [...targetGroups.values()];
      let nextGroupIndex = 0;

      const uploadNextTarget = async () => {
        while (!signal.aborted && nextGroupIndex < groups.length) {
          const group = groups[nextGroupIndex];
          nextGroupIndex += 1;
          // One worker owns each target's complete ordered sequence. Waiting for
          // the next file never consumes another worker or blocks other targets.
          for (const { entry, index, resolved, isPdf, signature } of group) {
            if (signal.aborted) break;
            try {
              const uploadUrl = `https://api.cloudinary.com/v1_1/${signature.cloudName}/${isPdf ? 'raw' : 'auto'}/upload`;
              const blob = await fetchBounded(resolved).then((response) => response.blob());
              const form = new FormData();
              form.append('file', blob, entry.fileName);
              form.append('api_key', signature.apiKey);
              form.append('timestamp', String(signature.timestamp));
              form.append('signature', signature.signature);
              form.append('folder', signature.folder);
              form.append('public_id', signature.public_id);
              form.append('overwrite', 'true');

              const upResp = await fetchBounded(uploadUrl, { method: 'POST', body: form });
              const upJson = await upResp.json().catch(() => null);
              if (!upResp.ok || !upJson?.secure_url) {
                throw new Error(upJson?.error?.message || `cloudinary_upload_${upResp.status}`);
              }

              uploaded[index] = {
                orderId,
                productSku: entry.product || 'general',
                productName: entry.productName || '',
                areaKey: entry.areaKey,
                method: entry.method,
                itemIndex: entry.itemIndex,
                originalName: entry.fileName,
                public_id: upJson.public_id,
                folder: signature.folder,
                secure_url: upJson.secure_url,
                resource_type: upJson.resource_type,
                bytes: upJson.bytes,
                format: upJson.format,
                colors: entry.colors,
                qty: entry.qty,
              };
            } catch (error) {
              recordFailure(entry, index, error);
            }
          }
        }
      };

      // Only distinct targets run concurrently. Bound decoding and uploading
      // together and settle every active worker before checkout can advance.
      const uploadWorkers = await Promise.allSettled(Array.from(
        { length: Math.min(MAX_CONCURRENT_CHECKOUT_UPLOADS, groups.length) },
        () => uploadNextTarget(),
      ));
      const failedUploadWorker = uploadWorkers.find((worker) => worker.status === 'rejected');
      if (failedUploadWorker) return { ok: false, error: failedUploadWorker.reason, files: [] };
      if (signal.aborted) throw signal.reason;
      return { ok: true, files: uploaded };
    }, { timeoutMs });
  } catch (error) {
    return { ok: false, error, files: [] };
  }
}
